import { supabase } from "@/integrations/supabase/client";

/**
 * Direct database operations for universal storage (like Content Generator pattern)
 */
export interface UniversalLibraryItemRecord {
  id: string;
  content_type: string;
  created_at: string;
  category_id: string;
  display_order?: number;
  // Foreign key columns
  workbook_image_id?: string;
  workbook_research_id?: string;
  workbook_page_id?: string;
  // Content data from joined tables
  image_data?: any;
  research_data?: any;
  workbook_data?: any;
  private_image_data?: any;
  private_research_data?: any; // NEW: Private research data from workbook_research table
  private_page_data?: any; // NEW: Private page data from workbook_page_content table
}

/**
 * Fetches all content from the universal_storage_items table with joins
 * Direct database operation without React Query wrapper
 */
export async function fetchUniversalStorageItems({ 
  contentTypes, 
  categoryId 
}: { 
  contentTypes?: string[];
  categoryId?: string | null;
}): Promise<UniversalLibraryItemRecord[]> {
  console.log('[UniversalStorage] Starting fetchUniversalStorageItems with params:', {
    contentTypes,
    categoryId,
    timestamp: new Date().toISOString()
  });

  // Use the optimized PostgreSQL function
  const { data, error } = await supabase
    .rpc('get_universal_storage_items', {
      p_content_types: contentTypes || null,
      p_category_id: categoryId || null
    });

  if (error) {
    console.error('[UniversalStorage] Error fetching universal storage items:', error);
    throw error;
  }

  console.log('[UniversalStorage] Raw data from PostgreSQL function:', {
    count: data?.length || 0,
    timestamp: new Date().toISOString()
  });

  if (!data || data.length === 0) {
    console.log('[UniversalStorage] No universal storage items found, returning empty array');
    return [];
  }

  // Count items by content type for logging
  const contentTypeCounts = data.reduce((acc: Record<string, number>, item: any) => {
    acc[item.content_type] = (acc[item.content_type] || 0) + 1;
    return acc;
  }, {});

  console.log('[UniversalStorage] Content type breakdown:', {
    ...contentTypeCounts,
    total: data.length
  });

  // The PostgreSQL function now returns data in the exact format we need
  // No additional mapping required - just validate and return
  const records: UniversalLibraryItemRecord[] = data.map((row: any) => {
    const record: UniversalLibraryItemRecord = {
      id: row.id,
      content_type: row.content_type,
      created_at: row.created_at,
      category_id: row.category_id,
      display_order: row.display_order,
      // Foreign key columns
      workbook_image_id: row.workbook_image_id || undefined,
      workbook_research_id: row.workbook_research_id || undefined,
      workbook_page_id: row.workbook_page_id || undefined,
      // Content data
      image_data: row.image_data || undefined,
      research_data: row.research_data || undefined,
      workbook_data: row.workbook_data || undefined,
      private_image_data: row.private_image_data || undefined,
      private_research_data: row.private_research_data || undefined,
      private_page_data: row.private_page_data || undefined,
    };

    // Log any records that don't have matching data
    const hasData = record.image_data || record.research_data || record.workbook_data || 
                   record.private_image_data || record.private_research_data || record.private_page_data;
    
    if (!hasData) {
      console.warn('[UniversalStorage] Record missing content data:', {
        storageId: row.id,
        contentType: row.content_type,
        workbook_image_id: row.workbook_image_id,
        workbook_research_id: row.workbook_research_id,
        workbook_page_id: row.workbook_page_id
      });
    }

    return record;
  });
  
  console.log('[UniversalStorage] Final processed records:', {
    totalRecords: records.length,
    recordsWithData: records.filter(r => 
      r.image_data || r.research_data || r.workbook_data || 
      r.private_image_data || r.private_research_data || r.private_page_data
    ).length,
    recordsWithoutData: records.filter(r => 
      !r.image_data && !r.research_data && !r.workbook_data && 
      !r.private_image_data && !r.private_research_data && !r.private_page_data
    ).length,
    timestamp: new Date().toISOString()
  });

  return records;
}

/**
 * Updates display order for a universal storage item
 * Direct database operation like Content Generator
 */
export async function updateUniversalStorageDisplayOrder(
  itemId: string,
  displayOrder: number
): Promise<void> {
  const { error } = await supabase
    .from('universal_storage_items')
    .update({ display_order: displayOrder })
    .eq('id', itemId);

  if (error) {
    console.error('Failed to update universal storage display order:', error);
    throw new Error(`Failed to update storage item order: ${error.message}`);
  }
} 